import {
  isAuthError,
  isForbiddenError,
  isSuccess,
} from '@/context/services/type';
import { normalizeServiceError } from '@/context/services/utils/extractServiceError';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListReconciliationSettingsQueryKey,
  useListReconciliationSettingsSuspense,
  useUpsertReconciliationSetting,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';

export const useGetReconciliationServiceSetting = () => {
  const { data, error } = useListReconciliationSettingsSuspense();
  if (error) throw error;

  if (isForbiddenError(data?.status) || isAuthError(data?.status)) {
    location.reload();
  }

  return useMemo(
    () => ({
      // The single-service reconciliation UI maps to the first row in the list.
      data: data?.data?.reconciliation_settings?.[0],
    }),
    [data]
  );
};

type TUpdateReconciliationSettingMutateArgs = ReturnType<
  typeof useUpsertReconciliationSetting
>['mutateAsync'];
type UpdateReconciliationSettingMutateArgs =
  Parameters<TUpdateReconciliationSettingMutateArgs>['0'];

type UpdateResult =
  | { status: 'success' }
  | {
      status: 'error';
      statusCode: number;
      code: string | undefined;
      messages: string[] | undefined;
    };

export const useUpdateReconciliationServiceSettingService = () => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync, isPending } =
    useUpsertReconciliationSetting({
      mutation: {
        onSuccess: () => {
          query.invalidateQueries({
            queryKey: getListReconciliationSettingsQueryKey(),
          });
        },
      },
    });
  const mutateAsync = useCallback(
    async (
      args: UpdateReconciliationSettingMutateArgs
    ): Promise<UpdateResult> => {
      const resp = await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        return { status: 'success' };
      }
      const { code, messages } = normalizeServiceError(resp.data);
      return { status: 'error', statusCode: resp.status, code, messages };
    },
    [_mutateAsync]
  );
  return { mutateAsync, isPending };
};
